import React, { useEffect, useState } from "react";
import {
  Stack,
  Grid,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import useDebounce from "../hooks/useDebounce";
import {
  createFiscalItem,
  updateFiscalItem,
  getCountries,
  getQuantityUnits,
  getPackageUnits,
  getTaxGroups,
  getItemClassCodes,
} from "../redux/actions/fiscalActions";
import { getProducts } from "../redux/actions/productActions";
import { cleanFormData } from "../helpers/formHelpers";

// Schema for Creation (includes initialStock)
const createSchema = Yup.object({
  productId: Yup.string().required("Product is required"),
  orgCountryCode: Yup.string().required("Country of Origin is required"),
  unitPrice: Yup.number().required("Unit Price is required").min(0),
  itemTypeCode: Yup.string().required("Item Type is required"),
  taxCode: Yup.string().required("Tax Code is required"),
  qtyUnitCode: Yup.string().required("Quantity Unit is required"),
  pkgUnitCode: Yup.string().required("Package Unit is required"),
  itemClassCode: Yup.string().required("Item Class Code is required"),
  initialStock: Yup.number()
    .min(0, "Stock cannot be negative")
    .required("Initial stock required"),
});

// Schema for Update (No initialStock, product is disabled)
const updateSchema = Yup.object({
  orgCountryCode: Yup.string().required("Country of Origin is required"),
  unitPrice: Yup.number().required("Unit Price is required").min(0),
  itemTypeCode: Yup.string().required("Item Type is required"),
  taxCode: Yup.string().required("Tax Code is required"),
  qtyUnitCode: Yup.string().required("Quantity Unit is required"),
  pkgUnitCode: Yup.string().required("Package Unit is required"),
  itemClassCode: Yup.string().required("Item Class Code is required"),
});

const CreateOrUpdateFiscalItemForm = ({
  item = null,
  isEditing = false,
  loading,
}) => {
  const dispatch = useDispatch();

  // --- Redux State ---
  const { products } = useSelector((state) => state.product);
  const { lookups, itemClassCodes, loadingItemClassCodes } = useSelector(
    (state) => state.fiscal,
  );

  // --- Local Search State for Item Class Code ---
  const [classCodeSearch, setClassCodeSearch] = useState("");
  const debouncedClassSearch = useDebounce(classCodeSearch, 500);

  // Load standard lookups on mount
  useEffect(() => {
    if (!products || products.length === 0) dispatch(getProducts());
    if (lookups.countries.data.length === 0) dispatch(getCountries());
    if (lookups.quantityUnits.data.length === 0) dispatch(getQuantityUnits());
    if (lookups.packageUnits.data.length === 0) dispatch(getPackageUnits());
    if (lookups.taxGroups.data.length === 0) dispatch(getTaxGroups());
  }, [dispatch, products, lookups]);

  // Load paginated Item Class Codes when user types
  useEffect(() => {
    dispatch(getItemClassCodes({ search: debouncedClassSearch, limit: 50 }));
  }, [dispatch, debouncedClassSearch]);

  // Option Mappers
  const productOpts = (products || []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const countryOpts = lookups.countries.data.map((c) => ({
    value: c.code,
    label: `${c.name} (${c.code})`,
  }));
  const qtyUnitOpts = lookups.quantityUnits.data.map((u) => ({
    value: u.code,
    label: `${u.code} - ${u.name}`,
  }));
  const pkgUnitOpts = lookups.packageUnits.data.map((u) => ({
    value: u.code,
    label: `${u.code} - ${u.name}`,
  }));
  const taxOpts = lookups.taxGroups.data.map((t) => ({
    value: t.code,
    label: `${t.name} (${t.rate}%)`,
  }));

  const itemTypeOpts = [
    { value: "1", label: "1 - Raw Material" },
    { value: "2", label: "2 - Finished Goods" },
    { value: "3", label: "3 - Services" },
  ];

  const initialValues = {
    productId: item?.productId || "",
    orgCountryCode: item?.orgCountryCode || "KE",
    unitPrice: item?.unitPrice || "",
    itemTypeCode: item?.typeCode || "3",
    taxCode: item?.taxCode || "",
    qtyUnitCode: item?.qtyUnitCode || "U",
    pkgUnitCode: item?.pkgUnitCode || "CT",
    itemClassCode: item?.itemClassCode || "",
    initialStock: 0,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isEditing ? updateSchema : createSchema}
      onSubmit={(values) => {
        const cleanedValues = cleanFormData(values);
        if (isEditing) {
          // Send original productId to the backend for linking
          cleanedValues.productId = item.productId;
          dispatch(updateFiscalItem(item.id, cleanedValues)); // Backend expects DB ID
        } else {
          dispatch(createFiscalItem(cleanedValues));
        }
      }}
    >
      {({ values, setFieldValue, isValid }) => (
        <Form>
          <Stack spacing={2}>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {isEditing ? (
                    <Box
                      p={1.5}
                      bgcolor="background.default"
                      borderRadius={1}
                      border="1px solid"
                      borderColor="divider"
                    >
                      <Typography variant="caption" color="text.secondary">
                        Editing eTIMS Config for Product:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {item.name}
                      </Typography>
                    </Box>
                  ) : (
                    <FormSelectField
                      name="productId"
                      label="Select Local Product"
                      options={productOpts}
                      searchable
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormSelectField
                    name="itemTypeCode"
                    label="Item Type"
                    options={itemTypeOpts}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormSelectField
                    name="taxCode"
                    label="Tax Code"
                    options={taxOpts}
                  />
                </Grid>

                {/* ASYNC AUTOCOMPLETE FOR ITEM CLASS CODE */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={itemClassCodes || []}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${option.code} - ${option.name}`
                    }
                    loading={loadingItemClassCodes}
                    value={
                      itemClassCodes.find(
                        (c) => c.code === values.itemClassCode,
                      ) || values.itemClassCode
                    }
                    onChange={(event, newValue) => {
                      setFieldValue(
                        "itemClassCode",
                        newValue ? newValue.code : "",
                      );
                    }}
                    onInputChange={(event, newInputValue) => {
                      setClassCodeSearch(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item Class Code (Search by Name/Code)"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormSelectField
                    name="orgCountryCode"
                    label="Country of Origin"
                    options={countryOpts}
                    searchable
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="unitPrice"
                    label="Unit Price"
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormSelectField
                    name="qtyUnitCode"
                    label="Quantity Unit"
                    options={qtyUnitOpts}
                    searchable
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormSelectField
                    name="pkgUnitCode"
                    label="Packaging Unit"
                    options={pkgUnitOpts}
                    searchable
                  />
                </Grid>

                {!isEditing && (
                  <Grid item xs={12}>
                    <FormTextField
                      name="initialStock"
                      label="Initial Stock Quantity"
                      type="number"
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <SubmitButton
              title={isEditing ? "Update eTIMS Item" : "Push Item to KRA eTIMS"}
              loading={loading}
              disabled={!isValid || loading}
              startIcon={isEditing ? <SaveOutlinedIcon /> : <CloudUploadIcon />}
              sx={{ mt: 1 }}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
export default CreateOrUpdateFiscalItemForm;
